<script>
import EditorControllersDock from '@/components/editor/EditorControllersDock'
import UserAvatar from '@/components/UserAvatar'

const HIDDEN_ROUTE_NAMES = new Set([
  'beta',
  'sign-in',
  'sign-up',
  'reset-password',
  'logout',
  'not-authorized',
  'not-found',
  'projects',
  'dataroom',
  'dataroom:overview',
  'dataroom:deck',
  'dataroom:product',
  'dataroom:market',
  'dataroom:financial-model',
  'dataroom:traction',
  'dataroom:roadmap',
  'dataroom:team',
  'dataroom:files',
  'dataroom:faq',
  'dataroom:admin',
])

export default {
  name: 'GlobalAppSidebar',
  components: {
    EditorControllersDock,
    UserAvatar,
  },
  data() {
    return {
      open: false,
    }
  },
  computed: {
    visible() {
      return !HIDDEN_ROUTE_NAMES.has(this.$route.name)
    },
    profileAvatar() {
      return this.$store.state.user.profile?.avatar || ''
    },
    unreadMessages() {
      return this.$store.getters['user/unreadMessagesNum'] || 0
    },
    leagueRoute() {
      return {
        name: 'league',
        query: {
          source: 'profile',
          panel: 'messages',
        },
      }
    },
  },
  watch: {
    '$route.fullPath'() {
      this.open = false
    },
  },
  methods: {
    openSidebar() {
      this.open = true
    },
    closeSidebar() {
      this.open = false
    },
    handleNavClick(e) {
      this.closeSidebar()
      e?.currentTarget?.blur?.()
    },
  },
}
</script>

<template>
  <div
    v-if="visible"
    class="global-app-sidebar"
    :class="{ 'is-open': open }"
    @mouseenter="openSidebar"
    @mouseleave="closeSidebar"
  >
    <router-link
      :to="{ name: 'profile' }"
      class="global-app-sidebar-avatar"
      title="My Profile"
      aria-label="My Profile"
      @focus="openSidebar"
      @blur="closeSidebar"
      @click="handleNavClick"
    >
      <UserAvatar :url="profileAvatar" :size="0.3" />
    </router-link>

    <aside class="global-app-sidebar-panel vyby-glass-panel vyby-glare" aria-label="Global navigation">
      <router-link
        :to="{ name: 'editor' }"
        class="global-app-sidebar-link"
        title="AI Studio"
        aria-label="AI Studio"
        @click="handleNavClick"
      >
        <span class="global-app-sidebar-nav-text">AI Studio</span>
      </router-link>

      <router-link
        :to="leagueRoute"
        class="global-app-sidebar-link"
        title="The League"
        aria-label="The League"
        @click="handleNavClick"
      >
        <span
          v-if="unreadMessages"
          class="global-app-sidebar-badge"
        >
          {{ unreadMessages }}
        </span>
        <span class="global-app-sidebar-nav-text">The League</span>
      </router-link>

      <div class="global-app-sidebar-divider" aria-hidden="true" />

      <EditorControllersDock class="global-app-sidebar-vybes" />
    </aside>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/vyby.scss';

.global-app-sidebar {
  position: fixed;
  top: 7px;
  left: 33px;
  z-index: 2147483000;
  width: 160px;
  pointer-events: auto;
}

.global-app-sidebar-avatar {
  position: relative;
  z-index: 2;
  display: inline-flex;
  width: 58px;
  height: 58px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  pointer-events: auto;
}

.global-app-sidebar-avatar:hover {
  text-decoration: none;
}

.global-app-sidebar-panel {
  position: absolute;
  top: 58px;
  left: -33px;
  width: 144px;
  padding: 12px 10px;
  border-radius: 0 18px 18px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  pointer-events: auto;
  opacity: 0;
  transform: translateX(-116%);
  transition:
    opacity 180ms ease,
    transform 220ms ease;
}

.global-app-sidebar.is-open .global-app-sidebar-panel,
.global-app-sidebar:focus-within .global-app-sidebar-panel {
  opacity: 1;
  transform: translateX(0);
}

.global-app-sidebar-link {
  position: relative;
  width: 112px;
  min-height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f37021;
  transition:
    background 160ms ease,
    transform 160ms ease;
}

.global-app-sidebar-link:hover,
.global-app-sidebar-link:focus-visible {
  background: radial-gradient(
    circle,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 42%,
    rgba(255, 255, 255, 0.5) 100%
  );
  transform: translateY(-1px);
  color: #f37021;
  text-decoration: none;
}

.global-app-sidebar-nav-text {
  color: #fff;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-align: center;
  text-transform: uppercase;
}

.global-app-sidebar-badge {
  position: absolute;
  top: 6px;
  right: 8px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #f00;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}

.global-app-sidebar-divider {
  width: calc(100% - 16px);
  height: 1px;
  background: rgba(255, 255, 255, 0.18);
}

.global-app-sidebar-vybes {
  flex: 0 0 auto;
}
</style>
